import * as yup from 'yup'
import uniqBy from 'lodash/uniqBy'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useTw } from '../../../../../utils/Styles'
import { Input, Button } from '../../../../../utils/UI'
import { useFormikContext, Formik, Form } from 'formik'
import { useSetGuestMutation } from './SetGuest.generated'
import { useCreateConversationMutation } from './CreateConversation.generated'
import { ConversationFragmentDoc } from '../../../../../api/Fragments/Conversation.generated'

const schema = yup.object({
  name: yup.string().trim().required(),
  email: yup.string().email().trim().required(),
})

export function Submit({ text }: { text: string }) {
  const { isValid, isSubmitting, submitForm } = useFormikContext()
  const canSubmit = isValid && !isSubmitting
  return <Button text={text} disabled={!canSubmit} onClick={submitForm} />
}

export function GuestSetup() {
  const tw = useTw()
  const navigate = useNavigate()
  const [setGuest] = useSetGuestMutation()
  const [createConvo] = useCreateConversationMutation({
    awaitRefetchQueries: true,
    refetchQueries: ['Conversations'],
  })

  const [t] = useTranslation('common')
  return (
    <Formik<yup.InferType<typeof schema>>
      validationSchema={schema}
      initialValues={{} as yup.InferType<typeof schema>}
      onSubmit={async ({ email, name }) => {
        await setGuest({
          variables: { data: { email: email.trim(), name: name.trim() } },
        })
        const res = await createConvo({
          update(cache, { data }) {
            cache.modify({
              fields: {
                Conversations(existing = []) {
                  const ref = cache.writeFragment({
                    data: data.CreateConversation,
                    fragmentName: 'Conversation',
                    fragment: ConversationFragmentDoc,
                    id: cache.identify(data.CreateConversation),
                  })

                  return uniqBy([ref, ...existing], '__ref')
                },
              },
            })
          },
        })
        navigate(`/conversation/${res.data.CreateConversation._id}`)
      }}
    >
      <Form className={tw`flex flex-col h-full justify-end`}>
        <div className={tw`mx-4 text-center`}>
          <span className={tw`select-none text-7xl`}>👋🏻</span>
          <h1 className={tw`text-2xl`}>
            {t('conversation.guest_setup.title')}
          </h1>
          <p className={tw`text-sm`}>{t('conversation.guest_setup.desc')}</p>
        </div>
        <div className={tw`mx-4`}>
          <Input
            label={t('conversation.guest_setup.fields.name')}
            name="name"
          />
          <Input
            label={t('conversation.guest_setup.fields.email')}
            name="email"
            type="email"
          />
          <div className={tw`ml-auto my-4`}>
            <Submit text={t('conversation.guest_setup.fields.send')} />
          </div>
        </div>
      </Form>
    </Formik>
  )
}
